import { memo, useCallback, useMemo } from "react";
import type { Moment } from "moment";
import { IAssignment } from "@miplatform/types";
import { subtractFromBusinessDate } from "@miplatform/utils";
import { AssignmentCard } from "./AssignmentCard";
import { AssignmentUserSortBy } from "./AssignmentsCalendar.constants";
import type { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import { AssignmentsColumn } from "./AssignmentsColumn";

interface AssignmentsUsersRendererProps {
  date: Moment;
  users: IAssignmentCalendarUser[];
  isCurrentDateOnly?: boolean;
  sortBy?: AssignmentUserSortBy;
  selectedUser: IAssignmentCalendarUser | null;
  minHeight?: number;
  onSelectedUserChange?: (user: IAssignmentCalendarUser | null) => void;
  onUnassignAssignment: (assignmentId: string) => void;
}

export const AssignmentsUsersRenderer = memo(
  ({
    date,
    users,
    isCurrentDateOnly,
    selectedUser,
    minHeight,
    onSelectedUserChange,
    onUnassignAssignment,
  }: AssignmentsUsersRendererProps) => {
    const userDates = useMemo(
      () => [date, subtractFromBusinessDate(date, 1)],
      [date]
    );

    const getUserHeaderClickHandler = useCallback(
      (user: IAssignmentCalendarUser) =>
        onSelectedUserChange ? () => onSelectedUserChange(user) : undefined,
      [onSelectedUserChange]
    );

    const handleResetSelectedUser = useCallback(
      () => onSelectedUserChange?.(null),
      [onSelectedUserChange]
    );

    const renderAssignmentCards = (assignments: IAssignment[]) =>
      assignments.map((assignment) => (
        <AssignmentCard
          key={assignment.id}
          assignment={assignment}
          isAssignDisabled
          onUnassign={onUnassignAssignment}
        />
      ));

    const renderUsers = () =>
      users.map((user) => {
        const publishedCurrentDateAssignments =
          user.currentDateAssignments.filter(({ publishedOn }) =>
            Boolean(publishedOn)
          );
        const additionalCount = [
          ...user.previousDateAssignments,
          ...user.currentDateAssignments,
        ].filter(
          ({ assigneeId, publishedOn }) => assigneeId && !publishedOn
        ).length;

        return (
          <AssignmentsColumn
            key={user.id}
            isSelected={selectedUser?.id === user.id}
            isDisabled={Boolean(selectedUser && selectedUser?.id !== user.id)}
            title={user.displayName}
            count={publishedCurrentDateAssignments.length}
            additionalCount={additionalCount}
            ordersCount={user.previousDateAssignments.length}
            minHeight={minHeight}
            sections={
              !isCurrentDateOnly
                ? userDates.map((date, index) => {
                    const assignments =
                      index === 0
                        ? user.currentDateAssignments
                        : user.previousDateAssignments;

                    return {
                      date,
                      count: assignments.length,
                      children:
                        assignments.length > 0
                          ? renderAssignmentCards(assignments)
                          : null,
                    };
                  })
                : undefined
            }
            onClick={
              selectedUser && selectedUser?.id !== user.id
                ? handleResetSelectedUser
                : undefined
            }
            onHeaderClick={
              selectedUser?.id === user.id
                ? handleResetSelectedUser
                : getUserHeaderClickHandler(user)
            }
          >
            {isCurrentDateOnly && user.currentDateAssignments.length > 0
              ? renderAssignmentCards(user.currentDateAssignments)
              : null}
          </AssignmentsColumn>
        );
      });

    return renderUsers();
  }
);
